import { Box, Stack } from '@mui/material'

import type { DraftJsContent } from '../../../../../../../pure-js/libs/Common'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import type { IndividualSessionFeedbackData } from '../../../../../types/IndividualSessionFeedbackTypes'
import { ProfileCardWithIndicators } from '../../../../feedbackGenerator/components'
import LatestFeedbackLayoutBox from '../../../../feedbackGenerator/components/LatestFeedbackLayoutBox'
import {
  EmotionStateSection,
  ImprovementIdeaVisualization
} from '../../../../feedbackGenerator/components/visualizations'
import type { LatestSessionFeedbackView } from '../../hooks/useLatestSessionFeedbackView'
import { SessionFeedbackSectionHeader } from '../../components/SessionFeedbackSectionHeader'
import { WatchOutSection } from '../../components/WatchOutSection'
import { WhatsWorkingSection } from '../../components/WhatsWorkingSection'

type CoachSessionFeedbackSectionsProps = {
  feedbackData: IndividualSessionFeedbackData
  view: LatestSessionFeedbackView
}

/**
 * The same feedback sections the full page shows, sized for a dialog beside a conversation. The page's
 * own `PageWrapper` layout is not reused, only the parts that carry the content and their derivations.
 */
export const CoachSessionFeedbackSections = ({ feedbackData, view }: CoachSessionFeedbackSectionsProps) => {
  const { t } = useTranslation()

  const hasProfile = view.profileIndicators.length > 0
  const hasImprovementIdea = Boolean(feedbackData.improvementIdea)
  const hasWhatsWorking = Boolean(feedbackData.whatIsWorkingWell)
  const hasWatchOut = Boolean(feedbackData.whatToWatchOut)

  return (
    <Stack spacing={{ xs: 2, sm: 2.5, md: 2.5 }} sx={{ width: '100%' }}>
      <SessionFeedbackSectionHeader sessionTitle={view.sessionTimeFormatted} />

      {/* Paired the way the page pairs them: the text cards carry a min-height so two of them line up. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: gridColumnsFor(hasProfile, hasImprovementIdea), gap: 2 }}>
        {hasProfile && (
          <ProfileCardWithIndicators
            profileName={view.profileTitle}
            description={view.profileDescription}
            indicators={view.profileIndicators}
          />
        )}

        {hasImprovementIdea && (
          <LatestFeedbackLayoutBox variant="team" sx={{ width: '100%', height: '100%' }}>
            <ImprovementIdeaVisualization
              title={t('gamer.feedback.improvementIdea.title', 'Try next time')}
              description={view.improvementVisualization.description}
              recommendation={view.improvementVisualization.recommendation}
              variant="individual"
            />
          </LatestFeedbackLayoutBox>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumnsFor(hasWhatsWorking, hasWatchOut),
          gap: 2,
          alignItems: 'stretch'
        }}
      >
        {hasWhatsWorking && <WhatsWorkingSection content={feedbackData.whatIsWorkingWell as DraftJsContent} />}
        {hasWatchOut && <WatchOutSection content={feedbackData.whatToWatchOut as DraftJsContent} />}
      </Box>

      {feedbackData.communicatedEmotion && (
        <Box sx={{ width: '100%' }}>
          <EmotionStateSection
            emotion={feedbackData.communicatedEmotion.dominantEmotion}
            exampleUtterance={feedbackData.communicatedEmotion.exampleUtterance}
            text={feedbackData.communicatedEmotion.text}
          />
        </Box>
      )}
    </Stack>
  )
}

/** Two columns when both cards are there, one when only one is — never a half-width card on its own. */
function gridColumnsFor(hasFirst: boolean, hasSecond: boolean) {
  const isPair = hasFirst && hasSecond
  return { xs: '1fr', sm: isPair ? '1fr 1fr' : '1fr', md: isPair ? '1fr 1fr' : '1fr' }
}
